import type { ReactNode } from 'react';
import type { ClientPortalCrumb } from 'src/features/client-portal/components/shell/types';
import { useState } from 'react';
import { useRouter } from 'next/router';
import { cn } from '@savvy-kit/lib/utils';
import { useSplashHold } from 'src/components/loading-and-splash/splash-hold';
import { ClientPortalShell } from 'src/features/client-portal/components/shell/client-portal-shell';
import { ClientPortalVersionPending } from 'src/features/client-portal/components/version-pending';
import { useClientPortalRedesign } from 'src/features/client-portal/hooks/use-client-portal-redesign';
import { useRedirectHiddenClientPortalRoute } from 'src/features/client-portal/hooks/use-redirect-hidden-client-portal-route';

import { loggedInAsAdvisorID } from '@savvy/deprecated-shared/utils/savvy-auth';

import { AdvisorImpersonationBar } from './dashboard-advisor-impersonation-bar';
import { DashboardNavbar } from './dashboard-navbar';
import DashboardSidebar from './dashboard-sidebar';
import { IMPERSONATION_BAR_HEIGHT_PX } from './impersonation-bar-height';

type DashboardLayoutProps = {
  children?: ReactNode;
  readonly headerTitle?: string;
  readonly leafCrumb?: string;
  readonly parentCrumb?: ClientPortalCrumb;
  readonly useClientHeader?: boolean;
  readonly hideBorder?: boolean;
  readonly headerBreadcrumbs?: React.ReactElement[];
  readonly headerRightContent?: ReactNode;
  readonly hideMobileBack?: boolean;
  readonly hideHeaderTitle?: boolean;
  readonly hideNavbar?: boolean;
  readonly hideNavbarOnDesktop?: boolean;
};

function getPaddingTop(hideNavbar?: boolean, isAdvisorLoggedInAsClient?: boolean): number {
  if (hideNavbar) {
    return isAdvisorLoggedInAsClient ? IMPERSONATION_BAR_HEIGHT_PX : 0;
  }
  return isAdvisorLoggedInAsClient ? IMPERSONATION_BAR_HEIGHT_PX * 2 : IMPERSONATION_BAR_HEIGHT_PX;
}

const PADDING_TOP_CLASS: Record<number, string> = {
  0: 'pt-0',
  [IMPERSONATION_BAR_HEIGHT_PX]: 'pt-16',
  [IMPERSONATION_BAR_HEIGHT_PX * 2]: 'pt-32',
};
const DESKTOP_PADDING_TOP_CLASS: Record<number, string> = {
  0: 'min-[1200px]:pt-0',
  [IMPERSONATION_BAR_HEIGHT_PX]: 'min-[1200px]:pt-16',
};

export function DashboardLayout(props: DashboardLayoutProps) {
  const {
    children,
    headerTitle,
    leafCrumb,
    parentCrumb,
    hideBorder,
    useClientHeader,
    headerBreadcrumbs,
    headerRightContent,
    hideMobileBack,
    hideHeaderTitle,
    hideNavbar,
    hideNavbarOnDesktop,
  } = props;
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);
  const router = useRouter();
  const { hideNav } = router.query;
  const isAdvisorLoggedInAsClient = typeof window !== 'undefined' ? !!loggedInAsAdvisorID() : false;
  const shellDecision = useClientPortalRedesign('shell');
  useRedirectHiddenClientPortalRoute();
  // The opening splash covers the wait instead of a shell that might be the wrong one.
  useSplashHold('client-portal-shell', shellDecision === 'pending' && !hideNav);

  if (hideNav) {
    return <>{children} </>;
  }

  if (shellDecision === 'pending') {
    return <ClientPortalVersionPending />;
  }

  if (shellDecision === 'redesign') {
    return (
      <ClientPortalShell
        headerTitle={headerTitle}
        leafCrumb={leafCrumb}
        parentCrumb={parentCrumb}
        headerRightContent={headerRightContent}
        hideMobileBack={hideMobileBack}
        hideHeaderTitle={hideHeaderTitle}
      >
        {children}
      </ClientPortalShell>
    );
  }

  return (
    <>
      <div
        className={cn(
          'flex max-w-full flex-auto bg-background min-[1200px]:pl-64 print:pt-0 print:pl-0',
          PADDING_TOP_CLASS[getPaddingTop(hideNavbar, isAdvisorLoggedInAsClient)],
          hideNavbarOnDesktop === true && DESKTOP_PADDING_TOP_CLASS[getPaddingTop(true, isAdvisorLoggedInAsClient)],
        )}
      >
        <div className="flex flex-[1_1_auto] flex-col w-full">{children}</div>
      </div>
      {isAdvisorLoggedInAsClient && (
        <div className="print:hidden">
          <AdvisorImpersonationBar />
        </div>
      )}
      {hideNavbar !== true && (
        <div className={cn('print:hidden', hideNavbarOnDesktop === true && 'lg:hidden')}>
          <DashboardNavbar
            onOpenSidebar={(): void => {
              setIsSidebarOpen(true);
            }}
            title={headerTitle}
            hideBorder={hideBorder}
            useClientHeader={useClientHeader}
            breadcrumbItems={headerBreadcrumbs}
            rightContent={headerRightContent}
          />
        </div>
      )}
      <div className="print:hidden">
        <DashboardSidebar
          onClose={(): void => {
            setIsSidebarOpen(false);
          }}
          open={isSidebarOpen}
        />
      </div>
    </>
  );
}
